// currency.ts — the session's money, as the game shows it (SC-61816).
//
// The currency is a property of the SESSION (the casino's launch, confirmed by the RGS in the ticket exchange and the
// balance read): never a build constant and never a default. Every amount on screen is formatted from the same integer
// minor units the RGS moves, with that currency.
//
// The vendored game's money model is "integer minor units, two decimals": the stake field speaks major units and
// multiplies by 100, its max-profit cap is 1,000,000 minor units, every figure divides by 100. So the game is correct
// only for a currency whose canonical exponent is 2, and it must not run for any other: a balance off by a power of ten
// is worse than no game. The list below mirrors the two-decimal entries of the RGS's frozen registry (rgs-core-runtime
// runtime/internal/domain/currency.go — append-only: a currency's exponent never changes). USDT is played in hundredths
// (SC-68493, below). Everything else the registry knows (JPY 0; KWD, BHD, OMR 3; USDC 6; BTC 8; ETH, CHP 18) and anything
// it does not know FAILS CLOSED here. The SDK's own currencyExponent() cannot decide this: it defaults an unknown currency
// to 2.
import { formatMinor } from "@blocklabsdev/rgs-game-manager-frontend-api";

const TWO_DECIMAL_CURRENCIES: ReadonlySet<string> = new Set(["EUR", "USD", "GBP", "CHF", "CAD", "AUD", "SEK", "NOK", "PLN", "BRL"]);

// SC-68493: a currency whose minor unit is finer than the game's hundredths, played IN hundredths. The game's money model
// stays two-decimal; only the boundary converts, by the factor between the wire's minor unit and a hundredth (the
// registry's exponent less 2). USDT alone (exponent 6, factor 10,000): TG Lab's pairs, where one credit is 10,000
// micro-USDT = 0.01 USDT (rgs-core-runtime launchmatrix, Q-h), so every stake and payout is a whole number of hundredths and
// the game's hundredths ARE the round's credits (its own replay of a roll counts in them). Intl has no currency style for
// a 4-letter code: such an amount reads "1,234.56 USDT".
const HUNDREDTHS_FACTOR: ReadonlyMap<string, number> = new Map([["USDT", 10_000]]);

/** Amounts are shown the way the stake field is typed: a dot for decimals, whatever the browser's locale. */
const LOCALE = "en-US";

export interface SessionMoney {
  readonly currency: string;
  /** The game's integer hundredths → the amount's text in the session currency ("€1,234.56", "1,234.56 USDT"). */
  readonly format: (minor: number) => string;
  /** What stands before the stake field's number: the currency's symbol, or its code when it has none ("€", "CHF", "USDT"). */
  readonly prefix: string;
  /** The wire's (the SDK's) integer minor units → the game's hundredths, truncated: a balance or a max stake with finer
   *  digits is never shown (or staked) above what there is. */
  readonly toGame: (wireMinor: number) => number;
  /** A payout in the wire's minor units → the game's hundredths, EXACTLY; undefined when it is not a whole number of them.
   *  The game adds payouts up (its profit, an autobet's stops), so a truncated one would drift: such a payout is one the
   *  game cannot show. A TG Lab payout is a whole hundredth once the runtime settles at TG Lab's precision (SC-68497
   *  criterion 3, Q6). */
  readonly payoutToGame: (wireMinor: number) => number | undefined;
  /** The game's hundredths → the wire's minor units, exactly (a stake). */
  readonly toWire: (gameMinor: number) => number;
  /** The wire's integer minor units → the amount's text with every digit it has (the settlement terms a ticket states:
   *  a max win, a credit's size). The same as `format` for a two-decimal currency. */
  readonly formatWire: (wireMinor: number) => string;
}

/** True when the game's two-decimal money model is correct for this currency (exact, case-sensitive ISO code), directly or
 *  in hundredths (SC-68493). */
export function playableCurrency(currency: string): boolean {
  return TWO_DECIMAL_CURRENCIES.has(currency) || HUNDREDTHS_FACTOR.has(currency);
}

const same = (minor: number): number => minor;

/** The session's money for a playable currency; null for any other (the caller must not run the game). */
export function sessionMoney(currency: string): SessionMoney | null {
  const factor = HUNDREDTHS_FACTOR.get(currency);
  if (factor !== undefined) return hundredths(currency, factor);
  if (!playableCurrency(currency)) return null;
  const symbol = new Intl.NumberFormat(LOCALE, { style: "currency", currency }).formatToParts(0).find((p) => p.type === "currency");
  return {
    currency,
    format: (minor) => formatMinor(minor, currency, LOCALE),
    prefix: symbol?.value ?? currency,
    toGame: same,
    payoutToGame: same,
    toWire: same,
    formatWire: (minor) => formatMinor(minor, currency, LOCALE),
  };
}

const HUNDREDTHS_TEXT = new Intl.NumberFormat(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function hundredths(currency: string, factor: number): SessionMoney {
  const digits = Math.round(Math.log10(factor)) + 2; // the wire's fraction digits: 6 for USDT
  const wireText = new Intl.NumberFormat(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: digits });
  return {
    currency,
    format: (minor) => `${HUNDREDTHS_TEXT.format(minor / 100)} ${currency}`,
    prefix: currency,
    toGame: (wireMinor) => Math.floor(wireMinor / factor),
    payoutToGame: (wireMinor) => (Number.isSafeInteger(wireMinor) && wireMinor % factor === 0 ? wireMinor / factor : undefined),
    toWire: (gameMinor) => {
      const wire = gameMinor * factor;
      if (!Number.isSafeInteger(wire)) throw new RangeError(`a stake of ${gameMinor} hundredths of ${currency} is not a whole amount`);
      return wire;
    },
    // Exact, from the integer digits (a float division would lose the last digit of a large amount).
    formatWire: (wireMinor) => `${wireText.format(exactDecimal(wireMinor, digits) as unknown as number)} ${currency}`,
  };
}

/** `minor / 10^digits` as an exact decimal string, the sign in front; an amount that is not a safe integer is divided. */
function exactDecimal(minor: number, digits: number): string | number {
  if (!Number.isSafeInteger(minor)) return minor / 10 ** digits;
  const sign = minor < 0 ? "-" : "";
  const n = BigInt(Math.abs(minor));
  const unit = 10n ** BigInt(digits);
  return `${sign}${n / unit}.${(n % unit).toString().padStart(digits, "0")}`;
}

/** A currency code safe to show in a notice (it is server-supplied text): short, upper-case letters and digits only. */
export function displayableCode(currency: string): string | null {
  return /^[A-Z0-9]{2,8}$/.test(currency) ? currency : null;
}
